import { useCallback, useEffect, useState } from 'react';
import type { Operaciones, ParDeTokens, Yo } from '../api/operaciones';
import { ErrorApi } from '../api/problema';
import { EstadoCargando, EstadoError, Hueco } from '../componentes/EstadoPagina';
import { faltanDosPasos } from '../logica/acceso';
import { estadoDeError } from '../logica/estados';
import { iniciales } from '../logica/navegacion';
import { useEsMovil } from '../marco/tamano';
import { RUTAS_ACCESO, RUTA_BACKOFFICE } from '../rutas';
import { useCifraErrores } from './errores/cifra';
import { MarcoBackoffice } from './marco/MarcoBackoffice';
import { SECCION_INICIAL, seccionDe, seccionesDe } from './secciones';
import { SinPermiso } from './SinPermiso';
import './backoffice.css';

/**
 * **Backoffice de plataforma** (1.20 a 1.22, `diseno/plataforma/1.20-1.22-backoffice.html`): la zona de las cuentas de
 * plataforma (`account.type = platform`), fuera de un negocio, que llegan aquí tras iniciar sesión (G133) con los dos
 * pasos activados (G65). El rol sale de `account.platform_role` (Superadmin o Soporte, G69): el menú enseña lo que el rol
 * puede ver (G120) y lo demás, si se llega por la dirección, es «sin permiso». Las secciones van en `?seccion=` y se
 * registran en `secciones.ts`; las que aún no están programadas (1.21, 1.22, 7.4) enseñan su hueco.
 */
interface Props {
    ops: Operaciones;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    onCerrarSesion: () => void;
    onSesionCaducada: () => void;
    onFaltanDosPasos: () => void;
    /** Con el acceso abierto (`openPlatformAccess`), entra en el negocio con sus tokens (G137). */
    onAccesoAbierto: (tokens: ParDeTokens) => void;
}

export function Backoffice({ ops, busqueda, navegar, onCerrarSesion, onSesionCaducada, onFaltanDosPasos, onAccesoAbierto }: Props) {
    const movil = useEsMovil();
    const [yo, setYo] = useState<Yo | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [vuelta, setVuelta] = useState(0);

    useEffect(() => {
        let vivo = true;
        ops.getMe()
            .then((datos) => {
                if (!vivo) {
                    return;
                }
                // Solo las cuentas de plataforma: las demás van a su sitio (G133).
                if (datos.account.type !== 'platform') {
                    navegar(RUTAS_ACCESO.elegirNegocio, true);
                    return;
                }
                if (!datos.account.two_factor_enabled) {
                    onFaltanDosPasos();
                    return;
                }
                setYo(datos);
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onSesionCaducada();
                    return;
                }
                if (faltanDosPasos(e)) {
                    onFaltanDosPasos();
                    return;
                }
                setError(e);
            });
        return () => {
            vivo = false;
        };
    }, [ops, vuelta, navegar, onSesionCaducada, onFaltanDosPasos]);

    const cambiarConsulta = useCallback(
        (cambios: Readonly<Record<string, string | null>>, reemplazar = false) => {
            const nueva = new URLSearchParams(busqueda);
            for (const [clave, valor] of Object.entries(cambios)) {
                if (valor === null) {
                    nueva.delete(clave);
                } else {
                    nueva.set(clave, valor);
                }
            }
            const texto = nueva.toString();
            navegar(texto === '' ? RUTA_BACKOFFICE : `${RUTA_BACKOFFICE}?${texto}`, reemplazar);
        },
        [busqueda, navegar],
    );

    const superadmin = yo?.account.platform_role === 'superadmin';
    const secciones = seccionesDe(superadmin);
    const seccion = seccionDe(busqueda);
    const permitida = secciones.some((s) => s.clave === seccion.clave);
    // La cifra de «Cola de errores» en el menú (1.21).
    const cifraErrores = useCifraErrores(ops, yo !== null, superadmin, seccion.clave);
    const ir = (clave: string) => {
        navegar(clave === SECCION_INICIAL ? RUTA_BACKOFFICE : `${RUTA_BACKOFFICE}?seccion=${clave}`);
    };

    const contenido = (() => {
        if (error !== null) {
            return (
                <EstadoError
                    seccion={seccion.etiqueta}
                    codigoSoporte={estadoDeError(error).codigoSoporte}
                    movil={movil}
                    onReintentar={() => {
                        setError(null);
                        setVuelta((v) => v + 1);
                    }}
                />
            );
        }
        if (yo === null) {
            return <EstadoCargando seccion={seccion.etiqueta} />;
        }
        if (!permitida) {
            return (
                <SinPermiso
                    seccion={seccion.etiqueta}
                    superadmin={superadmin}
                    movil={movil}
                    onIr={() => {
                        ir(SECCION_INICIAL);
                    }}
                />
            );
        }
        const { Pantalla } = seccion;
        if (Pantalla === null) {
            return <Hueco seccion={seccion.etiqueta} icono={seccion.icono} />;
        }
        return (
            <Pantalla
                ops={ops}
                yo={yo}
                superadmin={superadmin}
                movil={movil}
                busqueda={busqueda}
                cambiarConsulta={cambiarConsulta}
                onSesionCaducada={onSesionCaducada}
                onFaltanDosPasos={onFaltanDosPasos}
                onAccesoAbierto={onAccesoAbierto}
            />
        );
    })();

    const nombre = yo?.account.display_name ?? '';
    return (
        <MarcoBackoffice
            movil={movil}
            secciones={yo === null ? [] : secciones}
            actual={seccion.clave}
            cifras={{ errores: cifraErrores }}
            ir={ir}
            persona={{ nombre, email: yo?.account.email ?? '', iniciales: iniciales(nombre), rol: superadmin ? 'Superadmin de plataforma' : 'Soporte de plataforma' }}
            titulo={seccion.titulo}
            subtitulo={seccion.subtitulo}
            ayuda={seccion.ayuda}
            onCerrarSesion={onCerrarSesion}
        >
            {contenido}
        </MarcoBackoffice>
    );
}
